// Human-readable rendering of analytics-event metadata for the Events-Stream
// admin panel. The backend persists heterogeneous metadata per event type
// (see src/chatbot/api/admin/schemas.py::EventMetadataValue and the Firestore
// Schema section of src/chatbot/CLAUDE.md). This module turns that raw JSON
// into a compact summary string in the dashboard language.
//
// SECURITY: every branch returns PLAIN TEXT. The caller renders it as a text
// node (never dangerouslySetInnerHTML). URLs and profession names from the
// datastore are untrusted — we extract a short label and never reflect raw
// markup. The full JSON stays available via the cell's title attribute.

import { t } from "@/i18n";
import { formatDecimal, formatInt } from "@/lib/format";

// Heterogeneous per event type; values are JSON scalars (str | int | float |
// bool) but we read defensively, so `unknown` is the honest element type.
type Metadata = Record<string, unknown>;

// Read a metadata field as a trimmed string, tolerating the mixed scalar
// types the emit() signature allows (str | int | float | bool).
const asText = (v: unknown): string =>
  v === null || v === undefined ? "" : String(v).trim();

const asNumber = (v: unknown): number => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Pull a short, human label out of a source URL. Decodes a ProfessionName
// query param when present (junior.gateway.one listings), otherwise falls
// back to the last non-empty path segment. Returns the raw (decoded) URL
// only when nothing better can be extracted. Always inert text.
export const urlLabel = (rawUrl: string): string => {
  const url = rawUrl.trim();
  if (!url) return "";
  try {
    const parsed = new URL(url, "https://gateway.one");
    const prof = parsed.searchParams.get("ProfessionName");
    if (prof && prof.trim()) return prof.trim();
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      return decodeURIComponent(segments[segments.length - 1]);
    }
    return parsed.host || url;
  } catch {
    // Not a parseable URL — fall back to the last slash-delimited segment.
    const segments = url.split(/[?#]/)[0].split("/").filter(Boolean);
    const last = segments.length > 0 ? segments[segments.length - 1] : url;
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  }
};

// "Quelle: {label}" plus an optional "· Beruf {id}" suffix when a
// profession id rode along (source_clicked / rag_chunk_sanitized).
const formatSourceClicked = (m: Metadata): string => {
  const label = urlLabel(asText(m.source_url));
  const prof = asText(m.profession_id);
  const source = t("eventmeta.source");
  const base = label ? `${source}: ${label}` : source;
  return prof ? `${base} · ${t("eventmeta.profession")} ${prof}` : base;
};

const formatProfessionQueried = (m: Metadata): string => {
  const profession = t("eventmeta.profession");
  const name = asText(m.profession_name);
  if (name) return `${profession}: ${name}`;
  const id = asText(m.profession_id);
  return id ? `${profession}: ${id}` : profession;
};

const formatRagResult = (m: Metadata): string =>
  t("eventmeta.hits", {
    n: formatInt(asNumber(m.chunks)),
    score: formatDecimal(asNumber(m.max_score)),
  });

const formatGuardrailBlocked = (m: Metadata): string => {
  const stage = asText(m.stage) || "?";
  const reason = asText(m.reason) || "?";
  return `${stage}: ${reason}`;
};

const formatErrorOccurred = (m: Metadata): string => {
  const stage = asText(m.stage) || "?";
  const reason = asText(m.reason) || "?";
  return `${stage}: ${reason}`;
};

const formatCommuteInfo = (m: Metadata): string =>
  t("eventmeta.offersWithCommute", { n: formatInt(asNumber(m.offers)) });

const formatFeedbackGiven = (m: Metadata): string =>
  asText(m.feedback) === "positive" ? "👍" : "👎";

const formatRagSanitized = (m: Metadata): string => {
  const label = urlLabel(asText(m.source_url));
  const sanitized = t("eventmeta.sourceSanitized");
  return label ? `${sanitized}: ${label}` : sanitized;
};

const formatTurnFlagged = (m: Metadata): string => {
  const turn = asText(m.turn_id);
  const withReason = m.has_reason === true || asText(m.has_reason) === "true";
  const suffix = withReason ? t("eventmeta.withReason") : t("eventmeta.withoutReason");
  return turn ? `${t("eventmeta.turn")} ${turn.slice(0, 12)}… · ${suffix}` : suffix;
};

// Fallback for unknown event types that DO carry metadata: compact
// "k: v · k: v" — never a raw JSON dump with braces.
const formatGenericPairs = (m: Metadata): string =>
  Object.entries(m)
    .map(([k, v]) => `${k}: ${asText(v)}`)
    .join(" · ");

type Formatter = (m: Metadata) => string;

const FORMATTERS: Record<string, Formatter> = {
  source_clicked: formatSourceClicked,
  profession_queried: formatProfessionQueried,
  no_answer_found: formatRagResult,
  low_relevance_answer: formatRagResult,
  guardrail_blocked: formatGuardrailBlocked,
  error_occurred: formatErrorOccurred,
  commute_info_written: formatCommuteInfo,
  feedback_given: formatFeedbackGiven,
  rag_chunk_sanitized: formatRagSanitized,
  turn_flagged: formatTurnFlagged,
};

/**
 * Render an analytics event's metadata as a compact, inert string in the
 * dashboard language.
 *
 * @param eventType - the event_type discriminator (e.g. "source_clicked").
 * @param metadata - the raw metadata object from the event document.
 * @returns plain text suitable for a text node; "—" when there is nothing
 *   meaningful to show. NEVER HTML.
 */
export const formatEventMetadata = (
  eventType: string,
  metadata: Record<string, unknown> | null | undefined,
): string => {
  const m: Metadata = metadata ?? {};
  if (Object.keys(m).length === 0) return "—";
  const formatter = FORMATTERS[eventType];
  return formatter ? formatter(m) : formatGenericPairs(m);
};
